import type { SignupMetadata } from '@/services/marketing'
import { readSignupAttribution, clearSignupAttribution } from '@/utils/signupAttribution'
/* eslint-disable react-refresh/only-export-components */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { createContext, useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import type { User } from '@/services/auth'
import { authService } from '@/services/auth'

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string, turnstileToken: string) => Promise<void>
  register: (name: string, email: string, password: string, password_confirmation: string, turnstileToken: string, invitationToken?: string, metadata?: SignupMetadata) => Promise<any>
  logout: () => Promise<void>
  deleteAccount: (password?: string) => Promise<void>
  googleLogin: (metadata?: SignupMetadata) => void
  accountVerification: (token: string, email: string) => Promise<{ success: boolean; message: string } | undefined>
  updateUser: (updatedUser: Partial<User> | User) => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Check if user is already authenticated on app start
    const initializeAuth = async () => {
      const token = authService.getToken()
      const storedUser = authService.getUser()

      if (token && storedUser) {
        try {
          // Verify token is still valid by fetching profile
          const currentUser = await authService.getProfile()
          setUser(currentUser)
        } catch (error) {
          // Token is invalid, clear auth
          authService.clearAuth()
          setUser(null)
        }
      }
      setIsLoading(false)
    }

    initializeAuth()
  }, [])

  const login = async (email: string, password: string, turnstileToken: string) => {
    try {
      const response = await authService.login({ email, password, turnstile_token: turnstileToken })
      authService.setToken(response.data.token)
      authService.setUser(response.data.user)
      setUser(response.data.user)
    } catch (error: any) {
      throw new Error(`${error.response?.data?.message}. ${error.response?.data?.error}` || 'Login failed')
    }
  }

  const register = async (name: string, email: string, password: string, password_confirmation: string, turnstileToken: string, invitationToken?: string, metadata: SignupMetadata = {}) => {
    try {
      const response = await authService.register({
        name,
        email,
        password,
        password_confirmation,
        turnstile_token: turnstileToken,
        invitation_token: invitationToken,
        ...readSignupAttribution(),
        ...metadata
      })
      authService.setToken(response.data.token)
      authService.setUser(response.data.user)
      setUser(response.data.user)
      clearSignupAttribution()
      return response.data // Return the response data so caller can access board_id
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Registration failed')
    }
  }

  const logout = async () => {
    try {
      await authService.logout()
    } catch (error) {
      // Even if logout fails on server, clear local auth
      console.error('Logout error:', error)
    } finally {
      authService.clearAuth()
      setUser(null)
    }
  }

  const deleteAccount = async (password?: string) => {
    // Unlike logout(), a failure here (e.g. wrong password) must NOT clear
    // the session — let it propagate so the caller can show an inline error
    // and the user stays logged in to try again.
    await authService.deleteAccount(password)
    authService.clearAuth()
    setUser(null)
  }

  const googleLogin = async (metadata: SignupMetadata = {}) => {
    try {
      setIsLoading(true)
      console.log('Starting Google OAuth flow...')
      const oauthUrl = await authService.getGoogleAuthUrl({ ...readSignupAttribution(), ...metadata })
      console.log('Received OAuth URL:', oauthUrl)

      if (!oauthUrl || !oauthUrl.startsWith('https://accounts.google.com')) {
        console.error('Invalid OAuth URL received:', oauthUrl)
        setIsLoading(false)
        return
      }

      window.location.href = oauthUrl
    } catch (error) {
      console.error('Google OAuth error:', error)
      setIsLoading(false)
    }
  }

  const accountVerification = async (token: string, email: string) => {
    try {
      setIsLoading(true)
      const response = await authService.verifyAccount(token, email)
      return { success: response?.success == true ? true : false, message: response?.message ?? 'Verification failed' }
    } catch (error) {
      console.error('Account verification error:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const updateUser = (updatedUser: Partial<User> | User) => {
    setUser(currentUser => {
      const nextUser = currentUser ? { ...currentUser, ...updatedUser } : updatedUser as User
      authService.setUser(nextUser)
      return nextUser
    })
  }

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    deleteAccount,
    googleLogin,
    accountVerification,
    updateUser,

  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
